/**
 * mp3Recorder — Browser-side live mic recorder that encodes WAV→MP3 via lamejs.
 * Returns a Blob with type "audio/mp3" ready to upload.
 *
 * Usage:
 *   const rec = await createMp3Recorder();
 *   rec.start();
 *   ...
 *   const blob = await rec.stop(); // type 'audio/mp3'
 */
import * as lamejs from '@breezystack/lamejs';

const SAMPLE_RATE = 44100;
const BIT_RATE = 128;
const TARGET_RATE = 44100; // resample target

export async function createMp3Recorder() {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    const err = new Error('mediaDevices.getUserMedia not available');
    err.name = 'NotSupportedError';
    throw err;
  }

  // Ask for the mic stream — throws NotAllowedError if user denies.
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  });

  // Some Android WebViews block AudioContext until a user gesture — getUserMedia
  // itself counts as a gesture so this should be fine when called from onClick.
  const AudioCtor = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtor) {
    stream.getTracks().forEach((t) => t.stop());
    const err = new Error('AudioContext not supported');
    err.name = 'NotSupportedError';
    throw err;
  }
  const audioCtx = new AudioCtor({ sampleRate: TARGET_RATE });
  // Resume if the context was created in a suspended state (iOS Safari).
  if (audioCtx.state === 'suspended') {
    try { await audioCtx.resume(); } catch {}
  }

  const source = audioCtx.createMediaStreamSource(stream);
  const processor = audioCtx.createScriptProcessor(4096, 1, 1);
  const encoder = new lamejs.Mp3Encoder(1, audioCtx.sampleRate || SAMPLE_RATE, BIT_RATE);
  const chunks = [];
  let active = false;

  processor.onaudioprocess = (e) => {
    if (!active) return;
    const input = e.inputBuffer.getChannelData(0);
    // Convert Float32 [-1,1] → Int16
    const int16 = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
      const s = Math.max(-1, Math.min(1, input[i]));
      int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    const mp3buf = encoder.encodeBuffer(int16);
    if (mp3buf.length > 0) chunks.push(mp3buf);
  };

  source.connect(processor);
  processor.connect(audioCtx.destination);

  const start = () => { active = true; };

  const stop = () => new Promise((resolve) => {
    active = false;
    // Flush encoder
    const flush = encoder.flush();
    if (flush.length > 0) chunks.push(flush);
    try { processor.disconnect(); } catch {}
    try { source.disconnect(); } catch {}
    stream.getTracks().forEach((t) => t.stop());
    try { audioCtx.close(); } catch {}
    const blob = new Blob(chunks, { type: 'audio/mp3' });
    resolve(blob);
  });

  return { start, stop };
}
